import React, { useState } from 'react';
import { useAuth } from '../../providers/AuthProvider';
import Button from '../common/Button';
import styles from '../../styles/onboarding/OnboardingWalletExplanation.module.scss';

interface OnboardingWalletExplanationProps {
  onComplete: () => void;
}

const OnboardingWalletExplanation: React.FC<OnboardingWalletExplanationProps> = ({ onComplete }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { completeOnboarding } = useAuth();

  const handleComplete = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      await completeOnboarding();
      onComplete();
    } catch (error: any) {
      setError(error.message || 'Failed to complete onboarding');
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.walletContainer}>
      <div className={styles.walletHeader}>
        <p className={styles.walletSubtitle}>
          All credit line use two dedicated wallets for credit repayments and for fee payments. 
          Here's how they work:
        </p>
      </div>

      <div className={styles.walletsGrid}>
        {/* Repayment Wallet */}
        <div className={styles.walletCard}>
          <div className={styles.walletIcon}>
            <svg className={styles.walletSvg} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M8 12h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 8v8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M16 8l-4-4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          
          <h3 className={styles.walletType}>Repayment Wallet</h3>
          
          <div className={styles.walletPurpose}>
            <h4 className={styles.purposeTitle}>Used for:</h4>
            <p className={styles.purposeText}>
            Repaying your credit line balance. Send funds to this wallet when you want to pay down your outstanding credit. Direct your customers to pay into this wallet and your credit automatically is paid down.
            </p>
          </div>
          
          <div className={styles.walletFeatures}>
            <div className={styles.feature}>
              <svg className={styles.featureIcon} viewBox="0 0 16 16" fill="currentColor">
                <path d="M8 12a.5.5 0 0 0 .5-.5V5.707l2.146 2.147a.5.5 0 0 0 .708-.708l-3-3a.5.5 0 0 0-.708 0l-3 3a.5.5 0 1 0 .708.708L7.5 5.707V11.5a.5.5 0 0 0 .5.5z"/>
                <path d="M3 0h10a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2v-1h1v1a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v1H1V2a2 2 0 0 1 2-2z"/>
              </svg>
              <span className={styles.featureText}>Credit repayments</span>
            </div>
            <div className={styles.feature}>
              <svg className={styles.featureIcon} viewBox="0 0 16 16" fill="currentColor">
                <path d="M4 10.781c.148 1.667 1.513 2.85 3.591 3.003V15h1.043v-1.216c2.27-.179 3.678-1.438 3.678-3.3 0-1.59-.947-2.51-2.956-3.028l-.722-.187V3.467c1.122.11 1.879.714 2.07 1.616h1.47c-.166-1.6-1.54-2.748-3.54-2.875V1H7.591v1.233c-1.939.23-3.27 1.472-3.27 3.156 0 1.454.966 2.483 2.661 2.917l.61.162v4.031c-1.149-.17-1.94-.8-2.131-1.718H4zm3.391-3.836c-1.043-.263-1.6-.825-1.6-1.616 0-.944.704-1.641 1.8-1.828v3.495l-.2-.05zm1.591 1.872c1.287.323 1.852.859 1.852 1.769 0 1.097-.826 1.828-2.2 1.939V8.73l.348.086z"/>
              </svg>
              <span className={styles.featureText}>Principal payments</span>
            </div>
          </div>
        </div>

        {/* Fee Payment Wallet */}
        <div className={styles.walletCard}>
          <div className={styles.walletIcon}>
            <svg className={styles.walletSvg} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M16 10a4 4 0 0 1-8 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 14v-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M10 8h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          
          <h3 className={styles.walletType}>Fee Payment Wallet</h3>
          
          <div className={styles.walletPurpose}>
            <h4 className={styles.purposeTitle}>Used for:</h4>
            <p className={styles.purposeText}>
            Paying credit line fees and interest. This wallet is used only for credit fee payments.
            </p>
          </div>
          
          <div className={styles.walletFeatures}>
            <div className={styles.feature}>
              <svg className={styles.featureIcon} viewBox="0 0 16 16" fill="currentColor">
                <path d="M8 0a.5.5 0 0 1 .5.5v5.21l3.248 1.856a.5.5 0 0 1-.496.868L8 6.56 4.748 8.434a.5.5 0 1 1-.496-.868L7.5 5.71V.5A.5.5 0 0 1 8 0z"/>
                <path d="M8 16a8 8 0 1 0 0-16 8 8 0 0 0 0 16zM8 1a7 7 0 1 1 0 14A7 7 0 0 1 8 1z"/>
              </svg>
              <span className={styles.featureText}>Interest payments</span>
            </div>
            <div className={styles.feature}>
              <svg className={styles.featureIcon} viewBox="0 0 16 16" fill="currentColor">
                <path d="M14.5 3a.5.5 0 0 1 .5.5v9a.5.5 0 0 1-.5.5h-13a.5.5 0 0 1-.5-.5v-9a.5.5 0 0 1 .5-.5h13zm-13-1A1.5 1.5 0 0 0 0 3.5v9A1.5 1.5 0 0 0 1.5 14h13a1.5 1.5 0 0 0 1.5-1.5v-9A1.5 1.5 0 0 0 14.5 2h-13z"/>
                <path d="M7 5.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zM1 8.5c0 .828.672 0 1.5 0s1.5.172 1.5-1c0-1.172-.672-2-1.5-2S1 7.328 1 8.5z"/>
                <path d="M12 5.5a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 0 1h-2a.5.5 0 0 1-.5-.5zm0 2a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 0 1h-2a.5.5 0 0 1-.5-.5zm0 2a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 0 1h-1a.5.5 0 0 1-.5-.5z"/>
              </svg>
              <span className={styles.featureText}>Credit fees</span>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.importantNote}>
        <div className={styles.noteIcon}>💡</div>
        <div className={styles.noteContent}>
          <h4 className={styles.noteTitle}>Important</h4>
          <p className={styles.noteText}>
            Please note that each Credit Line is associated with one chain and a token.
            The two wallets should be used only for the chain and token associated with the Credit Line.
          </p>
        </div>
      </div>

      {error && (
        <div className={styles.errorMessage}>
          <div className={styles.errorIcon}>⚠️</div>
          <p className={styles.errorText}>{error}</p>
        </div>
      )}

      <div className={styles.actionSection}>
        <Button
          btnSize="lg"
          onClick={handleComplete}
          disabled={isLoading}
          isLoading={isLoading}
          isLoadingText="Completing setup..."
          text="Complete Setup & Go to Dashboard"
        />
      </div>
    </div>
  );
};

export default OnboardingWalletExplanation;
